import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../../components/Modal';
import { Button, VisitLocationMap } from '../../../../components';
import { InitialsAvatar, hhmm } from '../shared';
import { useDutyRoster } from '../DutyRosterContext';

/**
 * Where the carer clocked in and out, from the shift's sessions — the app saves
 * start_/end_ latitude, longitude and accuracy_m on each one (api
 * shift.controller). The first clock-in and the last clock-out, to match the
 * Actual Clock In / Clock Out times above it.
 */
const shiftLocation = (s) => {
    const sessions = (Array.isArray(s.sessions) ? s.sessions : [])
        .filter((x) => x?.started_at)
        .sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
    const point = (x, prefix) => {
        const lat = Number(x?.[`${prefix}_latitude`]);
        const lng = Number(x?.[`${prefix}_longitude`]);
        if (x?.[`${prefix}_latitude`] == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { latitude: lat, longitude: lng, accuracy_m: x[`${prefix}_accuracy_m`] ?? null };
    };
    const lastClosed = [...sessions].reverse().find((x) => x.ended_at);
    return {
        carer: point(sessions[0], 'start'),
        carer_end: s.end_time ? point(lastClosed, 'end') : null,
        ended: !!s.end_time,
        // Why there is no pin, when the app said (location off, refused, no signal).
        start_failure: sessions[0]?.start_location_failure || null,
        end_failure: lastClosed?.end_location_failure || null,
    };
};

const ShiftDetailsModal = () => {
    const { isShiftDetailsModalOpen, setIsShiftDetailsModalOpen, selectedShiftDetail, formatDateObj, getAttendanceMatch, fmtClock, submitShiftEdit } = useDutyRoster();

    // Inline "Edit times" form. Reset whenever a different shift is opened so
    // a half-typed edit never carries over to someone else's shift.
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({ clockIn: '', clockOut: '', note: '' });
    useEffect(() => {
        setEditing(false);
        const s = selectedShiftDetail?.dayShift;
        setEditForm({
            clockIn: fmtClock(s?.start_time) || '',
            clockOut: s?.end_time ? fmtClock(s.end_time) : '',
            note: '',
        });
    }, [selectedShiftDetail, isShiftDetailsModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Once per opened shift: the map redraws whenever its points change
    // identity, and this modal re-renders on every keystroke in Edit times.
    const location = useMemo(
        () => (selectedShiftDetail?.dayShift ? shiftLocation(selectedShiftDetail.dayShift) : null),
        [selectedShiftDetail],
    );

    const inputCls = 'w-full px-3 py-2 rounded-lg border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 outline-none text-[14px]';
    const range = (r) => (r ? `${fmtClock(r.start) || '—'} – ${r.end ? fmtClock(r.end) : '…'}` : null);
    const stampLabel = (iso) => {
        const d = new Date(iso);
        return `${d.toLocaleDateString([], { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    };

    return (
            <Modal
                isOpen={isShiftDetailsModalOpen}
                onClose={() => setIsShiftDetailsModalOpen(false)}
                title="Shift Attendance Details"
                maxWidth="max-w-lg"
            >
                {selectedShiftDetail && (() => {
                    const s = selectedShiftDetail.dayShift;
                    const start = new Date(s.start_time);
                    const end = s.end_time ? new Date(s.end_time) : new Date();
                    const breakMins = s.break_time_minutes || 0;
                    const travelMins = s.travel_time_minutes || 0;
                    // session_minutes / worked_minutes come from the backend
                    // when the shift uses multi-session JSON. They correctly
                    // exclude the gaps between sessions. Fall back to the
                    // legacy elapsed-time formula for older single-session rows.
                    const totalMinutes = typeof s.session_minutes === 'number'
                        ? s.session_minutes
                        : Math.max(0, Math.floor((end - start) / (1000 * 60)));
                    const netMinutes = typeof s.worked_minutes === 'number'
                        ? s.worked_minutes
                        : Math.max(0, totalMinutes - breakMins);
                    const fmt = (mins) => `${Math.floor(mins / 60)}h ${mins % 60}m`;

                    const isOngoing = !s.end_time;
                    const onBreak = !!s.break_started_at;
                    const travelling = !!s.travel_started_at;
                    // Show the travel pane for any shift that actually has travel data
                    const showTravel = travelMins > 0 || travelling;

                    let statusLabel = 'Completed';
                    let statusClasses = 'bg-emerald-100 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400';
                    let statusDot = 'bg-emerald-500';
                    if (isOngoing) {
                        statusLabel = onBreak ? 'On Break' : travelling ? 'Travelling' : 'Working';
                        statusClasses = onBreak ? 'bg-orange-100 dark:bg-orange-900/10 text-orange-700 dark:text-orange-400'
                            : travelling ? 'bg-indigo-100 dark:bg-indigo-900/10 text-indigo-700 dark:text-indigo-400'
                                : 'bg-amber-100 dark:bg-amber-900/10 text-amber-700 dark:text-amber-400';
                        statusDot = onBreak ? 'bg-orange-500'
                            : travelling ? 'bg-indigo-500'
                                : 'bg-amber-500';
                    }

                    return (
                        <div className="space-y-5">
                            {/* Header: avatar + name + date + status pill */}
                            <div className="flex items-center gap-4 bg-gray-50 dark:bg-[#20242c] p-4 rounded-2xl border border-gray-100 dark:border-gray-800">
                                <InitialsAvatar name={selectedShiftDetail.emp.name} size="lg" />
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-[18px] font-bold text-gray-900 dark:text-gray-100 truncate">{selectedShiftDetail.emp.name}</h3>
                                    <p className="text-gray-500 dark:text-gray-400 text-[13px]">{formatDateObj(selectedShiftDetail.date).monthDate} {formatDateObj(selectedShiftDetail.date).dayExt}</p>
                                </div>
                                <span className={`text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-tight flex items-center gap-1.5 ${statusClasses}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${statusDot} ${isOngoing ? 'animate-pulse' : ''}`}></span>
                                    {statusLabel}
                                </span>
                            </div>

                            {/* Scheduled (rostered) window + on-time vs differs.
                                Shows what the carer was rostered to work so the
                                manager can compare it against the actual times below. */}
                            {(() => {
                                const roster = selectedShiftDetail.emp.rosterDetails;
                                const schedStart = roster?.shift_start;
                                const schedEnd = roster?.shift_end;
                                if (!schedStart || !schedEnd) return null;
                                const match = getAttendanceMatch(selectedShiftDetail.emp, s);
                                return (
                                    <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-[#20242c] rounded-2xl border border-slate-100 dark:border-gray-800">
                                        <div>
                                            <p className="text-[11px] text-slate-500 dark:text-gray-400 font-bold uppercase tracking-wider mb-1">Scheduled</p>
                                            <p className="text-[18px] font-black text-slate-800 dark:text-gray-200">{hhmm(schedStart)} – {hhmm(schedEnd)}</p>
                                        </div>
                                        {/* An approved shift is settled — show that
                                            rather than the raw clock-gap verdict, so
                                            this matches the grid cell. */}
                                        {s.hours_approved_at ? (
                                            <span className="text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-tight bg-emerald-100 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400">Approved</span>
                                        ) : (
                                            <>
                                                {match === 'matched' && (
                                                    <span className="text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-tight bg-emerald-100 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400">On time</span>
                                                )}
                                                {match === 'differ' && (
                                                    <span className="text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-tight bg-amber-100 dark:bg-amber-900/10 text-amber-700 dark:text-amber-400">Differs from schedule</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Actual Clock In / Clock Out */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-2xl border border-emerald-100 dark:border-emerald-900/50">
                                    <p className="text-[11px] text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-wider mb-1">Actual Clock In</p>
                                    <p className="text-[20px] font-black text-emerald-700 dark:text-emerald-400">
                                        {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                                <div className={`p-4 rounded-2xl border ${s.end_time ? 'bg-rose-50/50 dark:bg-red-900/10 border-rose-100 dark:border-red-900/50' : 'bg-amber-50/50 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/50'}`}>
                                    <p className={`text-[11px] font-bold uppercase tracking-wider mb-1 ${s.end_time ? 'text-rose-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>Actual Clock Out</p>
                                    <p className={`text-[20px] font-black ${s.end_time ? 'text-rose-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                                        {s.end_time
                                            ? new Date(s.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                            : 'Running…'}
                                    </p>
                                </div>
                            </div>

                            {/* Where the carer was at clock-in and clock-out —
                                recorded by the app with each QR scan. */}
                            <VisitLocationMap subject="shift" location={location} />

                            {/* Totals grid: total / net / break / [travel] */}
                            <div className={`grid ${showTravel ? 'grid-cols-2' : 'grid-cols-2'} gap-3`}>
                                <div className="p-4 bg-blue-50/40 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-900/50">
                                    <p className="text-[11px] text-blue-600 dark:text-blue-400 font-bold uppercase tracking-wider mb-1">
                                        Total {isOngoing && <span className="text-blue-400">(so far)</span>}
                                    </p>
                                    <p className="text-[16px] font-black text-blue-900 dark:text-blue-300">{fmt(totalMinutes)}</p>
                                </div>
                                <div className="p-4 bg-purple-50/40 dark:bg-purple-900/10 rounded-2xl border border-purple-100 dark:border-purple-900/50">
                                    <p className="text-[11px] text-purple-600 dark:text-purple-400 font-bold uppercase tracking-wider mb-1">Net Worked</p>
                                    <p className="text-[16px] font-black text-purple-900 dark:text-purple-300">{fmt(netMinutes)}</p>
                                </div>
                                <div className="p-4 bg-orange-50/40 dark:bg-orange-900/10 rounded-2xl border border-orange-100 dark:border-orange-900/50">
                                    <p className="text-[11px] text-orange-600 dark:text-orange-400 font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                                        Break {onBreak && <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse"></span>}
                                    </p>
                                    <p className="text-[16px] font-black text-orange-900 dark:text-orange-300">{breakMins} min</p>
                                </div>
                                {showTravel && (
                                    <div className="p-4 bg-indigo-50/40 dark:bg-indigo-900/10 rounded-2xl border border-indigo-100 dark:border-indigo-900/50">
                                        <p className="text-[11px] text-indigo-600 dark:text-indigo-400 font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                                            Travel {travelling && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></span>}
                                        </p>
                                        <p className="text-[16px] font-black text-indigo-900 dark:text-indigo-300">{travelMins} min</p>
                                    </div>
                                )}
                            </div>

                            {/* Currently-active banner — only when something is in flight right now */}
                            {(onBreak || travelling) && (
                                <div className={`p-3 rounded-xl border flex items-center gap-3 ${onBreak ? 'bg-orange-50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-900/50' : 'bg-indigo-50 dark:bg-indigo-900/10 border-indigo-200 dark:border-indigo-900/50'}`}>
                                    <div className={`w-2 h-2 rounded-full animate-pulse ${onBreak ? 'bg-orange-500' : 'bg-indigo-500'}`}></div>
                                    <span className={`text-[12px] font-bold ${onBreak ? 'text-orange-800 dark:text-orange-400' : 'text-indigo-800 dark:text-indigo-400'}`}>
                                        {onBreak
                                            ? `On break since ${new Date(s.break_started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                                            : `Travelling since ${new Date(s.travel_started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                                    </span>
                                </div>
                            )}

                            {s.notes && (
                                <div className="space-y-2">
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">Shift Notes</p>
                                    <div className="p-4 bg-gray-50 dark:bg-[#20242c] rounded-2xl text-[14px] text-gray-700 dark:text-gray-200 leading-relaxed italic border border-gray-100 dark:border-gray-800">
                                        "{s.notes}"
                                    </div>
                                </div>
                            )}

                            {/* Change history — every approval / edit of the
                                clocked times, oldest first, with its reason. */}
                            {Array.isArray(s.hours_change_log) && s.hours_change_log.length > 0 && (
                                <div className="space-y-2">
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">Change history</p>
                                    <ol className="space-y-2">
                                        {s.hours_change_log.map((c, i) => {
                                            const fromLabel = range(c.from);
                                            const toLabel = range(c.to);
                                            const unchanged = fromLabel && fromLabel === toLabel;
                                            return (
                                                <li key={i} className={`p-3 rounded-xl border text-[13px] ${c.__draft ? 'border-dashed border-blue-300 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10' : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-[#20242c]'}`}>
                                                    <div className="font-bold text-gray-800 dark:text-gray-200">
                                                        {!fromLabel
                                                            ? <>Hours entered: {toLabel}</>
                                                            : unchanged
                                                                ? <>Approved as recorded: {toLabel}</>
                                                                : <>Changed <span className="line-through text-gray-400 font-semibold">{fromLabel}</span> → {toLabel}</>}
                                                    </div>
                                                    {c.note && <div className="mt-1 text-gray-600 dark:text-gray-300 italic">"{c.note}"</div>}
                                                    <div className="mt-1 text-[11px] text-gray-400">
                                                        {c.__draft ? 'Unpublished draft' : `${c.by_name || 'Manager'} · ${stampLabel(c.at)}`}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ol>
                                </div>
                            )}

                            {/* Edit times — available on any finished shift,
                                including ones already approved and published. */}
                            {!isOngoing && editing && (
                                <div className="rounded-2xl border border-blue-100 dark:border-blue-900/50 bg-blue-50/40 dark:bg-blue-900/20 p-4 space-y-3">
                                    <h4 className="text-[13px] font-bold text-[#1E3A8A] dark:text-blue-300">Edit clocked times</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[#475569] dark:text-gray-400 mb-1">Clock-in</label>
                                            <input type="time" value={editForm.clockIn} onChange={(e) => setEditForm((f) => ({ ...f, clockIn: e.target.value }))} className={inputCls} />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[#475569] dark:text-gray-400 mb-1">Clock-out</label>
                                            <input type="time" value={editForm.clockOut} onChange={(e) => setEditForm((f) => ({ ...f, clockOut: e.target.value }))} className={inputCls} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[#475569] dark:text-gray-400 mb-1">Reason for change (required)</label>
                                        <textarea rows={2} value={editForm.note} onChange={(e) => setEditForm((f) => ({ ...f, note: e.target.value }))}
                                            placeholder="e.g. Carer forgot to clock out — confirmed with nurse in charge"
                                            className={`${inputCls} text-[13px] resize-none`} />
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <Button variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
                                        <Button
                                            variant="primary"
                                            disabled={!editForm.clockIn || !editForm.clockOut || !editForm.note.trim()}
                                            onClick={() => submitShiftEdit(selectedShiftDetail.emp, selectedShiftDetail.date, editForm.clockIn, editForm.clockOut, editForm.note)}
                                        >
                                            Save change
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-2">
                                {!isOngoing && !editing && (
                                    <Button variant="outline" block onClick={() => setEditing(true)}>
                                        Edit times
                                    </Button>
                                )}
                                <Button
                                    variant="secondary"
                                    block
                                    onClick={() => setIsShiftDetailsModalOpen(false)}
                                >
                                    Close Details
                                </Button>
                            </div>
                        </div>
                    );
                })()}
            </Modal>
    );
};

export default ShiftDetailsModal;
